import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import {
  MessageList, Message, MessageSeparator, TypingIndicator,
} from '@chatscope/chat-ui-kit-react';
import api, { getUser, clearTokens } from '../api';
import useSocket from '../hooks/useSocket';
import FileUpload from '../components/FileUpload';
import ImageLightbox from '../components/ImageLightbox';
import ClientInfo from '../components/ClientInfo';
import ChatMessageInput from '../components/ChatMessageInput';
import DOMPurify from 'dompurify';
import { MessageSquarePlus, Archive, ArchiveRestore, Bell, BellOff, Settings, Shield, Power, Search, X, Info, ArrowLeft, Paperclip, Download, Trash2, Pencil, UserPlus, UserMinus, Copy, Forward } from 'lucide-react';
import './Chat.css';

function VideoAttachment({ url, filename, size }) {
  const [play, setPlay] = useState(false);
  if (play) {
    return (
      <div>
        <video src={url} controls autoPlay className="chat-video" />
        <a href={url} download={filename} className="chat-file" target="_blank" rel="noopener noreferrer">
          ⬇ Скачать ({(size / 1024 / 1024).toFixed(1)} МБ)
        </a>
      </div>
    );
  }
  return (
    <div className="chat-video-wrap">
      <div className="chat-video-placeholder" onClick={() => setPlay(true)}>
        <span className="chat-video-icon">▶</span>
        <span className="chat-video-label">Видео ({(size / 1024 / 1024).toFixed(1)} МБ)</span>
      </div>
      <a href={url} download={filename} className="chat-file" target="_blank" rel="noopener noreferrer">
        ⬇ Скачать
      </a>
    </div>
  );
}

export default function Chat() {
  const navigate = useNavigate();
  const user = getUser();
  const [pushEnabled, setPushEnabled] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState({});
  const [typingUsers, setTypingUsers] = useState({});
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [hasMore, setHasMore] = useState({});
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [newChatModal, setNewChatModal] = useState(false);
  const [archivedRooms, setArchivedRooms] = useState([]);
  const [showArchive, setShowArchive] = useState(false);
  const [roomContextMenu, setRoomContextMenu] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [chatListFilter, setChatListFilter] = useState('');
  const [membersPopup, setMembersPopup] = useState(false);
  const [addMemberMode, setAddMemberMode] = useState(false);
  const [addMemberUsers, setAddMemberUsers] = useState([]);
  const [clientInfoVisible, setClientInfoVisible] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [chatGroups, setChatGroups] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const raw = localStorage.getItem('chatGroupCollapsed');
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  const [contextMenu, setContextMenu] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [forwardMessage, setForwardMessage] = useState(null);
  const activeRoomRef = useRef(null);
  const typingTimeouts = useRef({});
  const idempotencyRef = useRef(0);
  const notifSound = useRef(null);
  const longPressTimerRef = useRef(null);
  // Флаг «long-press только что выстрелил» — сбрасывается window-обработчиком click
  // (см. ниже). Используется чтобы: 1) не открывать lightbox по img.onClick после
  // удержания, 2) не закрывать только что открытое контекстное меню эмулированным
  // mouse click после touchend.
  const longPressFiredRef = useRef(false);
  const editTextareaRef = useRef(null);
  const chatMessagesRef = useRef(null);

  // === Scroll position persistence ===
  const SCROLL_LS_KEY = 'vetchat_scroll';
  const MAX_SCROLL_ROOMS = 50;

  const loadScrollMap = useCallback(() => {
    try {
      const raw = localStorage.getItem(SCROLL_LS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  }, []);

  const saveScrollPosition = useCallback((roomId, scrollTop) => {
    if (!roomId) return;
    try {
      const map = loadScrollMap();
      map[roomId] = scrollTop;
      const keys = Object.keys(map);
      if (keys.length > MAX_SCROLL_ROOMS) {
        delete map[keys[0]];
      }
      localStorage.setItem(SCROLL_LS_KEY, JSON.stringify(map));
    } catch { /* ignore */ }
  }, [loadScrollMap]);

  const getScrollPosition = useCallback((roomId) => {
    if (!roomId) return null;
    try {
      const map = loadScrollMap();
      return map[roomId] ?? null;
    } catch { return null; }
  }, [loadScrollMap]);

  const getScrollWrapper = useCallback(() => {
    return chatMessagesRef.current?.querySelector('.cs-message-list__scroll-wrapper')
      || document.querySelector('.chat-messages .cs-message-list__scroll-wrapper');
  }, []);

  const scrollToSavedOrBottom = useCallback((roomId) => {
    initialScrollDoneRef.current = false;
    setTimeout(() => {
      const wrapper = getScrollWrapper();
      if (!wrapper) {
        initialScrollDoneRef.current = true;
        return;
      }
      const saved = getScrollPosition(roomId);
      if (saved != null) {
        wrapper.scrollTop = saved;
      } else {
        wrapper.scrollTop = wrapper.scrollHeight;
      }
      initialScrollDoneRef.current = true;
    }, 250);
  }, [getScrollWrapper, getScrollPosition]);

  const [showScrollDown, setShowScrollDown] = useState(false);
  const initialScrollDoneRef = useRef(false);

  const checkScrollDownBtn = useCallback(() => {
    const wrapper = getScrollWrapper();
    if (!wrapper) return;
    const isNearBottom = wrapper.scrollTop + wrapper.clientHeight >= wrapper.scrollHeight - 300;
    setShowScrollDown(!isNearBottom);
  }, [getScrollWrapper]);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      const wrapper = chatMessagesRef.current?.querySelector('.cs-message-list__scroll-wrapper')
        || document.querySelector('.chat-messages .cs-message-list__scroll-wrapper');
      if (!wrapper) return;
      wrapper.scrollTop = wrapper.scrollHeight;
    }, 100);
  }, []);

  useEffect(() => {
    let ctx = null;
    const initSound = () => {
      if (ctx) return;
      try {
        ctx = new (window.AudioContext || window.webkitAudioContext)();
        notifSound.current = () => {
          if (ctx.state === "suspended") ctx.resume();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.value = 800;
          gain.gain.value = 0.1;
          osc.start();
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
          osc.stop(ctx.currentTime + 0.3);
        };
      } catch (e) {}
    };
    // Инициализируем по первому клику (требование браузера)
    document.addEventListener("click", initSound, { once: true });
    document.addEventListener("keydown", initSound, { once: true });
    return () => {
      document.removeEventListener("click", initSound);
      document.removeEventListener("keydown", initSound);
    };
  }, []);

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  // Check actual push subscription on mount
  useEffect(() => {
    if ('serviceWorker' in navigator && 'PushManager' in window && Notification.permission === 'granted') {
      navigator.serviceWorker.ready.then((reg) => {
        reg.pushManager.getSubscription().then((sub) => {
          setPushEnabled(!!sub);
        });
      });
    }
  }, []);

  const socket = useSocket({
    onConnect: (sock) => {
      const syncRooms = rooms.map((r) => ({ room_id: r.id, last_read_message_id: r.last_read_message_id || null }));
      if (syncRooms.length > 0) {
        sock.emit('sync', { rooms: syncRooms }, (result) => {
          if (result?.rooms) {
            setMessages((prev) => {
              const next = { ...prev };
              for (const [roomId, msgs] of Object.entries(result.rooms)) {
                const existing = next[roomId] || [];
                const existingIds = new Set(existing.map((m) => m.id));
                const newMsgs = msgs.filter((m) => !existingIds.has(m.id));
                next[roomId] = [...existing, ...newMsgs];
              }
              return next;
            });
          }
        });
      }
    },
    onMessage: ({ message, room_id }) => {
      setMessages((prev) => {
        const existing = prev[room_id] || [];
        if (existing.find((m) => m.id === message.id)) return prev;
        const tempIdx = existing.findIndex((m) => m.id?.startsWith?.('temp-') && m.sender_id === message.sender_id && m.body === message.body);
        if (tempIdx !== -1) {
          const updated = [...existing];
          updated[tempIdx] = { ...message, _sending: false };
          return { ...prev, [room_id]: updated };
        }
        return { ...prev, [room_id]: [...existing, message] };
      });
      if (room_id === activeRoomRef.current) {
        scrollToBottom();
      }
      const isFromOther = message.sender_id !== user?.id;
      if (isFromOther && notifSound.current) {
        const isMuted = rooms.find((r) => r.id === room_id)?.muted_at;
        if (!isMuted) {
          try { notifSound.current(); } catch (e) {}
        }
      }
      setRooms((prev) => {
        const updated = prev.map((r) => {
          if (r.id !== room_id) return r;
          return {
            ...r,
            last_message_body: message.body,
            last_message_at: message.created_at,
            last_message_sender_name: message.sender_name,
            last_message_type: message.type,
            unread_count: (room_id !== activeRoom && isFromOther) ? (r.unread_count || 0) + 1 : r.unread_count,
          };
        });
        return [...updated].sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0));
      });
      if (room_id === activeRoom && isFromOther) {
        socket.markRead({ room_id, message_id: message.id });
      }
    },
    onMessageRead: ({ room_id, user_id }) => {
      setRooms((prev) => prev.map((r) => r.id === room_id && user_id === user?.id ? { ...r, unread_count: 0, marked_unread: false } : r));
    },
    onTyping: ({ room_id, user_id, name }) => {
      if (user_id === user?.id) return;
      const key = `${room_id}-${user_id}`;
      setTypingUsers((prev) => ({ ...prev, [key]: name }));
      clearTimeout(typingTimeouts.current[key]);
      typingTimeouts.current[key] = setTimeout(() => {
        setTypingUsers((prev) => { const next = { ...prev }; delete next[key]; return next; });
      }, 3000);
    },
    onMessageDeleted: ({ message_id, room_id }) => {
      setMessages((prev) => ({ ...prev, [room_id]: (prev[room_id] || []).map((m) => m.id === message_id ? { ...m, deleted_at: new Date().toISOString() } : m) }));
      setTimeout(() => {
        setMessages((prev) => {
          const updated = (prev[room_id] || []).filter((m) => m.id !== message_id);
          const lastMsg = updated.filter((m) => !m.deleted_at).pop();
          setRooms((prevRooms) => prevRooms.map((r) => r.id === room_id ? { ...r, last_message_body: lastMsg?.body || '', last_message_at: lastMsg?.created_at || r.created_at, last_message_sender_name: lastMsg?.sender_name || '' } : r));
          return { ...prev, [room_id]: updated };
        });
      }, 3000);
    },
    onMessageEdited: ({ message_id, room_id, body }) => {
      setMessages((prev) => ({ ...prev, [room_id]: (prev[room_id] || []).map((m) => m.id === message_id ? { ...m, body, updated_at: new Date().toISOString() } : m) }));
    },
    onUserOnline: ({ user_id }) => { setOnlineUsers((prev) => new Set([...prev, user_id])); },
    onUserOffline: ({ user_id }) => { setOnlineUsers((prev) => { const next = new Set(prev); next.delete(user_id); return next; }); },
    onUsersOnline: ({ user_ids }) => { setOnlineUsers(new Set(user_ids)); },
    onAuthExpired: () => { clearTokens(); navigate('/login'); },
  });

  useEffect(() => {
    if (!user) return;
    api.get('/rooms').then((res) => setRooms(res.data.rooms));
    api.get('/chat-groups').then((res) => setChatGroups(res.data.groups || [])).catch(() => {});
    socket.connect();
    return () => socket.disconnect();
  }, []); // eslint-disable-line

  const toggleGroupCollapsed = (groupId) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
      try { localStorage.setItem('chatGroupCollapsed', JSON.stringify(Array.from(next))); } catch {}
      return next;
    });
  };

  useEffect(() => {
    if (!activeRoom) return;

    // Подписываемся на скролл текущей комнаты
    const wrapper = getScrollWrapper();
    const onScroll = () => {
      saveScrollPosition(activeRoom, wrapper?.scrollTop ?? 0);
      checkScrollDownBtn();
    };
    wrapper?.addEventListener('scroll', onScroll, { passive: true });

    if (messages[activeRoom]) {
      const msgs = messages[activeRoom];
      const lastMsg = msgs.filter((m) => !m.deleted_at).pop();
      if (lastMsg) socket.markRead({ room_id: activeRoom, message_id: lastMsg.id });
      setRooms((prev) => prev.map((r) => r.id === activeRoom ? { ...r, unread_count: 0, marked_unread: false } : r));
      scrollToSavedOrBottom(activeRoom);
    } else {
      setLoadingMessages(true);
      api.get(`/rooms/${activeRoom}/messages?limit=50`).then((res) => {
        setMessages((prev) => ({ ...prev, [activeRoom]: res.data.messages }));
        setHasMore((prev) => ({ ...prev, [activeRoom]: res.data.has_more }));
        const msgs = res.data.messages;
        if (msgs.length > 0) socket.markRead({ room_id: activeRoom, message_id: msgs[msgs.length - 1].id });
        setRooms((prev) => prev.map((r) => r.id === activeRoom ? { ...r, unread_count: 0, marked_unread: false } : r));
      }).finally(() => {
        setLoadingMessages(false);
        scrollToSavedOrBottom(activeRoom);
      });
    }

    return () => {
      wrapper?.removeEventListener('scroll', onScroll);
    };
  }, [activeRoom]); // eslint-disable-line

  const loadMore = useCallback(() => {
    if (!activeRoom || loadingMessages || !hasMore[activeRoom]) return;
    if (!initialScrollDoneRef.current) return;
    const roomMsgs = messages[activeRoom] || [];
    if (roomMsgs.length === 0) return;
    const oldest = roomMsgs[0];
    const wrapper = getScrollWrapper();
    const oldScrollTop = wrapper?.scrollTop ?? 0;
    const oldScrollHeight = wrapper?.scrollHeight ?? 0;
    setLoadingMessages(true);
    api.get(`/rooms/${activeRoom}/messages?cursor_time=${oldest.created_at}&cursor_id=${oldest.id}&limit=50`)
      .then((res) => {
        setMessages((prev) => ({ ...prev, [activeRoom]: [...res.data.messages, ...(prev[activeRoom] || [])] }));
        setHasMore((prev) => ({ ...prev, [activeRoom]: res.data.has_more }));
      }).finally(() => {
        setLoadingMessages(false);
        setTimeout(() => {
          const w = getScrollWrapper();
          if (w) {
            const diff = w.scrollHeight - oldScrollHeight;
            w.scrollTop = oldScrollTop + diff;
            saveScrollPosition(activeRoom, w.scrollTop);
          }
        }, 50);
      });
  }, [activeRoom, messages, loadingMessages, hasMore, getScrollWrapper, saveScrollPosition]);

  const handleSend = useCallback((text) => {
    if (!text.trim() || !activeRoom) return;
    const idempotencyKey = `${user.id}-${Date.now()}-${++idempotencyRef.current}`;
    const optimistic = { id: `temp-${idempotencyKey}`, room_id: activeRoom, sender_id: user.id, sender_name: user.name, sender_role: user.role, body: text, type: 'text', created_at: new Date().toISOString(), _sending: true };
    setMessages((prev) => ({ ...prev, [activeRoom]: [...(prev[activeRoom] || []), optimistic] }));
    scrollToBottom();
    socket.sendMessage({ room_id: activeRoom, body: text, type: 'text', idempotency_key: idempotencyKey }, (result) => {
      if (result?.message) {
        setMessages((prev) => ({ ...prev, [activeRoom]: (prev[activeRoom] || []).map((m) => m.id === optimistic.id ? { ...result.message, _sending: false } : m) }));
      } else {
        setMessages((prev) => ({ ...prev, [activeRoom]: (prev[activeRoom] || []).map((m) => m.id === optimistic.id ? { ...m, _sending: false, _failed: true } : m) }));
      }
    });
  }, [activeRoom, user, socket, scrollToBottom]);

  const retryMessage = useCallback((msg) => {
    setMessages((prev) => ({ ...prev, [activeRoom]: (prev[activeRoom] || []).filter((m) => m.id !== msg.id) }));
    handleSend(msg.body);
  }, [activeRoom, handleSend]);

  const handleContextMenu = useCallback((e, msg) => {
    e.preventDefault();
    if (msg.deleted_at || msg._sending || msg._failed) return;
    const isOwn = msg.sender_id === user?.id;
    const x = Math.min(e.clientX, window.innerWidth - 180);
    const y = Math.min(e.clientY, window.innerHeight - 100);
    setContextMenu({ x, y, msg, isOwn });
  }, [user]);

  // Long-press на мобилке: 500ms hold без движения → открыть то же меню, что и ПКМ.
  const startLongPress = useCallback((e, msg) => {
    if (msg.deleted_at || msg._sending || msg._failed) return;
    const touch = e.touches?.[0];
    if (!touch) return;
    const startX = touch.clientX;
    const startY = touch.clientY;
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      longPressFiredRef.current = true;
      const isOwn = msg.sender_id === user?.id;
      const x = Math.min(startX, window.innerWidth - 180);
      const y = Math.min(startY, window.innerHeight - 100);
      setContextMenu({ x, y, msg, isOwn });
      if (navigator.vibrate) { try { navigator.vibrate(20); } catch (_) {} }
    }, 500);
  }, [user]);
  const cancelLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  // Window click закрывает контекстное меню. Если только что был long-press,
  // эмулированный после touchend click НЕ должен схлопывать только что открытое меню,
  // поэтому первый click после long-press съедаем здесь и сбрасываем флаг.
  useEffect(() => {
    const close = () => {
      if (longPressFiredRef.current) {
        longPressFiredRef.current = false;
        return;
      }
      setContextMenu(null);
    };
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, []);

  const handleCopy = useCallback((msg) => {
    setContextMenu(null);
    const text = msg.body?.replace(/<[^>]+>/g, '') || '';
    navigator.clipboard.writeText(text).catch(() => {});
  }, []);

  const handleDownload = useCallback((msg) => {
    setContextMenu(null);
    if (msg.attachments?.length > 0) {
      msg.attachments.forEach((att) => {
        const a = document.createElement('a');
        a.href = '/uploads/' + att.key;
        a.download = att.filename;
        a.click();
      });
    }
  }, []);

  const handleForward = useCallback((msg) => {
    setContextMenu(null);
    setForwardMessage(msg);
  }, []);

  const forwardToRoom = useCallback((targetRoomId) => {
    if (!forwardMessage) return;
    const hasAttachments = forwardMessage.attachments?.length > 0;
    const originalBody = forwardMessage.body && !forwardMessage.attachments?.some(a => a.filename === forwardMessage.body) ? forwardMessage.body : '';
    const body = originalBody
      ? `<b>Переслано от ${forwardMessage.sender_name}:</b>\n${originalBody}`
      : `<b>Переслано от ${forwardMessage.sender_name}</b>`;
    const attachmentIds = forwardMessage.attachments?.map((a) => a.id) || [];
    socket.sendMessage({
      room_id: targetRoomId,
      body,
      type: attachmentIds.length > 0 ? 'file' : 'text',
      idempotency_key: `${user.id}-fwd-${Date.now()}`,
      attachment_ids: attachmentIds,
    }, () => {});
    setForwardMessage(null);
  }, [forwardMessage, socket, user]);

  const handleDelete = useCallback((msg) => {
    setContextMenu(null);
    if (!window.confirm('Удалить сообщение?')) return;
    socket.deleteMessage(msg.id, (result) => { if (result?.error) alert('Ошибка удаления'); });
  }, [socket]);

  const handleEditStart = useCallback((msg) => { setContextMenu(null); setEditingMessage({ id: msg.id, body: msg.body }); }, []);
  const handleEditSave = useCallback((newBody) => {
    if (!editingMessage || !newBody.trim()) return;
    socket.editMessage(editingMessage.id, newBody.trim(), (result) => { if (result?.error) alert('Ошибка редактирования'); });
    setEditingMessage(null);
  }, [editingMessage, socket]);
  const autoResizeEditTextarea = useCallback(() => {
    const el = editTextareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  }, []);

  const handleEditCancel = useCallback(() => { setEditingMessage(null); }, []);

  useEffect(() => {
    if (editingMessage) {
      const timer = setTimeout(autoResizeEditTextarea, 50);
      return () => clearTimeout(timer);
    }
  }, [editingMessage, autoResizeEditTextarea]);

  const openNewChat = async () => {
    try { const res = await api.get('/users?limit=100'); setAllUsers(res.data.users.filter((u) => u.id !== user.id)); setNewChatModal(true); } catch (e) { console.error(e); }
  };
  const createDirectChat = async (targetId) => {
    try { const res = await api.post('/rooms', { type: 'direct', member_ids: [targetId] }); setNewChatModal(false); const roomsRes = await api.get('/rooms'); setRooms(roomsRes.data.rooms); setActiveRoom(res.data.room.id); } catch (e) { alert('Ошибка создания чата'); }
  };

  const openAddMember = async () => {
    try {
      const res = await api.get('/users?limit=100');
      const currentMemberIds = new Set([user.id, ...(currentRoom?.other_members?.map((m) => m.id) || [])]);
      setAddMemberUsers(res.data.users.filter((u) => !currentMemberIds.has(u.id)));
      setAddMemberMode(true);
    } catch (e) { console.error(e); }
  };

  const handleAddMember = async (userId) => {
    try {
      await api.post('/rooms/' + activeRoom + '/members', { user_id: userId });
      const res = await api.get('/rooms');
      setRooms(res.data.rooms);
      setAddMemberMode(false);
    } catch (e) { alert(e.response?.data?.error?.message || 'Ошибка'); }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Удалить участника из чата?')) return;
    try {
      await api.delete('/rooms/' + activeRoom + '/members/' + userId);
      const res = await api.get('/rooms');
      setRooms(res.data.rooms);
    } catch (e) { alert(e.response?.data?.error?.message || 'Ошибка'); }
  };

  const archiveRoom = async (roomId) => {
    setRoomContextMenu(null);
    const archivedChat = rooms.find((r) => r.id === roomId);
    await api.post('/rooms/' + roomId + '/archive');
    setRooms((prev) => prev.filter((r) => r.id !== roomId));
    if (archivedChat) setArchivedRooms((prev) => [{ ...archivedChat, archived_at: new Date().toISOString() }, ...prev]);
    if (activeRoom === roomId) setActiveRoom(null);
  };
  const deleteRoomAction = async (roomId) => {
    setRoomContextMenu(null);
    if (!window.confirm('Удалить чат со всеми сообщениями? Это нельзя отменить.')) return;
    try {
      await api.delete('/rooms/' + roomId);
      setRooms((prev) => prev.filter((r) => r.id !== roomId));
      if (activeRoom === roomId) setActiveRoom(null);
    } catch (e) {
      alert(e.response?.data?.error?.message || 'Ошибка удаления');
    }
  };

  const unarchiveRoom = async (roomId) => {
    await api.post('/rooms/' + roomId + '/unarchive');
    setArchivedRooms((prev) => prev.filter((r) => r.id !== roomId));
    const res = await api.get('/rooms'); setRooms(res.data.rooms);
  };
  const loadArchived = async () => { const res = await api.get('/rooms/archived'); setArchivedRooms(res.data.rooms); setShowArchive(true); };

  const enablePush = () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) { alert('Push не поддерживается'); return; }
    Notification.requestPermission().then((permission) => {
      if (permission !== 'granted') { alert('Уведомления заблокированы'); return; }
      navigator.serviceWorker.register('/sw.js').then((registration) => {
        api.get('/push/vapid-key').then((res) => {
          const vapidPublicKey = res.data.publicKey;
          if (!vapidPublicKey) return;
          const padding = '='.repeat((4 - vapidPublicKey.length % 4) % 4);
          const base64 = (vapidPublicKey + padding).replace(/-/g, '+').replace(/_/g, '/');
          const rawData = window.atob(base64);
          const arr = new Uint8Array(rawData.length);
          for (let i = 0; i < rawData.length; i++) arr[i] = rawData.charCodeAt(i);
          registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: arr }).then((subscription) => {
            api.post('/push/subscribe', { subscription: subscription.toJSON(), device_id: navigator.userAgent.substring(0, 100) }).then(() => { setPushEnabled(true); alert('Уведомления включены!'); });
          });
        });
      });
    }).catch((err) => { alert('Ошибка: ' + err.message); });
  };

  const disablePush = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await api.post('/push/unsubscribe', { endpoint: subscription.endpoint });
        await subscription.unsubscribe();
      }
      setPushEnabled(false);
    } catch (err) { console.error(err); }
  };

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (!q || q.length < 2 || !activeRoom) { setSearchResults([]); return; }
    try { const res = await api.get('/rooms/' + activeRoom + '/search?q=' + encodeURIComponent(q)); setSearchResults(res.data.messages); } catch (e) { console.error(e); }
  };

  const handleRoomContextMenu = (e, room) => {
    e.preventDefault();
    const x = Math.min(e.clientX, window.innerWidth - 180);
    const y = Math.min(e.clientY, window.innerHeight - 60);
    setRoomContextMenu({ x, y, room });
  };
  useEffect(() => { const close = () => setRoomContextMenu(null); window.addEventListener('click', close); return () => window.removeEventListener('click', close); }, []);

  const markRoomUnread = async (roomId) => {
    try {
      await api.post('/rooms/' + roomId + '/mark-unread');
      setRooms((prev) => prev.map((r) => r.id === roomId ? { ...r, marked_unread: true, unread_count: Math.max(r.unread_count || 0, 1) } : r));
    } catch (e) { console.error(e); }
    setRoomContextMenu(null);
  };

  const toggleMuteRoom = async (roomId) => {
    try {
      const res = await api.post('/rooms/' + roomId + '/toggle-mute');
      setRooms((prev) => prev.map((r) => r.id === roomId ? { ...r, muted_at: res.data.muted ? new Date().toISOString() : null } : r));
    } catch (e) { console.error(e); }
    setRoomContextMenu(null);
  };

  const handleFileUploaded = useCallback((attachment) => {
    if (!activeRoom) return;
    const idempotencyKey = `${user.id}-file-${Date.now()}`;
    socket.sendMessage({ room_id: activeRoom, body: attachment.filename, type: 'file', idempotency_key: idempotencyKey, attachment_ids: [attachment.id] }, () => {});
  }, [activeRoom, user, socket]);

  // Свайпы для мобилки
  const touchStart = useRef(null);
  const handleTouchStart = useCallback((e) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, []);
  const handleTouchEnd = useCallback((e) => {
    if (!touchStart.current) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    const dy = e.changedTouches[0].clientY - touchStart.current.y;
    if (Math.abs(dx) < 60 || Math.abs(dy) > Math.abs(dx)) return;
    if (dx > 0) {
      setSidebarVisible(true);
    } else {
      if (activeRoom) setSidebarVisible(false);
    }
    touchStart.current = null;
  }, [activeRoom]);

  const typingThrottle = useRef({});
  const handleTyping = useCallback(() => {
    if (!activeRoom) return;
    const now = Date.now();
    if (now - (typingThrottle.current[activeRoom] || 0) < 2000) return;
    typingThrottle.current[activeRoom] = now;
    socket.sendTyping(activeRoom);
  }, [activeRoom, socket]);

  useEffect(() => { activeRoomRef.current = activeRoom; }, [activeRoom]);

  // Перехват системного back на Android. Без этого back уводил бы на /login,
  // где залогиненный пользователь видит форму и думает, что его «выкинуло».
  // Логика: накатываем дублирующую history entry; на popstate — если открыт
  // чат, закрываем его (визуально это как «назад к списку»), иначе остаёмся
  // на странице. Затем снова накатываем trap, чтобы следующий back тоже
  // перехватить. В системе остаётся возможность уйти через UI-кнопки
  // (Настройки / Админка / Выход) — они идут через navigate, а не через back.
  useEffect(() => {
    window.history.pushState({ chatBackTrap: true }, '');
    const onPop = () => {
      if (activeRoomRef.current) {
        setActiveRoom(null);
        setSidebarVisible(true);
      }
      window.history.pushState({ chatBackTrap: true }, '');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const selectRoom = useCallback((roomId) => {
    setActiveRoom(roomId);
    setSearchOpen(false); setSearchQuery(''); setSearchResults([]);
    if (window.innerWidth < 768) setSidebarVisible(false);
  }, []);

  const handleLogout = async () => {
    try { await api.post('/auth/logout'); } catch (e) {}
    clearTokens(); socket.disconnect(); navigate('/login');
  };

  // Helpers
  const currentRoom = rooms.find((r) => r.id === activeRoom);
  const currentMessages = messages[activeRoom] || [];

  const getRoomName = (room) => {
    if (room.name) return room.name;
    if (room.other_members?.length > 0) return room.other_members.map((m) => m.name).join(', ');
    return 'Чат';
  };

  const getRoomAvatarSrc = (room, name) => {
    if (room.type === 'direct') {
      const peer = room.other_members?.[0];
      if (peer?.avatar_url) return peer.avatar_url;
    } else if (room.avatar_url) {
      return room.avatar_url;
    }
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=AD182C&color=fff&size=44`;
  };

  const canEditRoomAvatar = (room) => {
    if (!room || room.type === 'direct') return false;
    if (user.role === 'admin') return true;
    if (['doctor', 'assistant'].includes(user.role) && room.is_member) return true;
    return false;
  };

  const uploadRoomAvatar = async (roomId, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Файл больше 5 МБ'); return; }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/rooms/${roomId}/avatar`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setRooms((prev) => prev.map((r) => r.id === roomId ? { ...r, avatar_key: res.data.avatar_key, avatar_url: res.data.avatar_url } : r));
    } catch (e) { alert('Ошибка: ' + (e.response?.data?.error?.message || e.message)); }
  };

  const removeRoomAvatar = async (roomId) => {
    if (!window.confirm('Удалить аватар чата?')) return;
    try {
      await api.delete(`/rooms/${roomId}/avatar`);
      setRooms((prev) => prev.map((r) => r.id === roomId ? { ...r, avatar_key: null, avatar_url: null } : r));
    } catch (e) { alert('Ошибка: ' + (e.response?.data?.error?.message || e.message)); }
  };

  const getClientId = () => {
    if (!currentRoom || !currentRoom.other_members) return null;
    const client = currentRoom.other_members.find((m) => m.role === 'client');
    return client ? client.id : null;
  };

  const formatTime = (dateStr) => { if (!dateStr) return ''; return new Date(dateStr).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }); };
  const formatDate = (dateStr) => {
    const d = new Date(dateStr); const today = new Date();
    if (d.toDateString() === today.toDateString()) return 'Сегодня';
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Вчера';
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
  };
  const stripHtml = (str) => { if (!str) return ''; return str.replace(/<[^>]*>/g, '').substring(0, 100); };

  const activeTyping = Object.entries(typingUsers).filter(([key]) => key.startsWith(`${activeRoom}-`)).map(([, name]) => name);

  // Grouped messages
  const groupedMessages = [];
  let lastDate = null;
  for (const msg of currentMessages) {
    if (msg.deleted_at && (Date.now() - new Date(msg.deleted_at).getTime() > 5000)) continue;
    const date = new Date(msg.created_at).toDateString();
    if (date !== lastDate) { groupedMessages.push({ type: 'separator', date: msg.created_at }); lastDate = date; }
    groupedMessages.push({ type: 'message', data: msg });
  }

  // Filter chat list
  const filteredRooms = chatListFilter ? rooms.filter((r) => getRoomName(r).toLowerCase().includes(chatListFilter.toLowerCase())) : rooms;

  // Разделение на группы и «прочие» чаты
  const groupedRoomIds = new Set();
  chatGroups.forEach((g) => (g.rooms || []).forEach((id) => groupedRoomIds.add(id)));
  const ungroupedRooms = filteredRooms.filter((r) => !groupedRoomIds.has(r.id));
  const roomById = new Map(filteredRooms.map((r) => [r.id, r]));

  // Title с количеством новых
  useEffect(() => {
    const totalUnread = rooms.reduce((sum, r) => sum + (r.unread_count || 0), 0);
    if (totalUnread > 0) {
      const word = totalUnread === 1 ? 'новое сообщение' : totalUnread < 5 ? 'новых сообщения' : 'новых сообщений';
      document.title = totalUnread + ' ' + word + ' — VetChat';
    } else {
      document.title = 'VetChat';
    }
  }, [rooms]);

  const userInitials = user ? user.name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() : '';

  if (!user) return null;

  return (
    <div className="chat-page" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      {/* ====== Left Icon Panel ====== */}
      <div className="icon-panel">
        <div className="icon-panel-avatar">{userInitials}</div>
        <div className="icon-panel-menu">
          {!sidebarVisible && activeRoom && (
            <button className="icon-panel-btn icon-panel-back" onClick={() => setSidebarVisible(true)} title="Назад"><ArrowLeft size={20} /></button>
          )}
          {(user.role === 'admin' || user.role === 'doctor' || user.role === 'assistant') && (
            <button className="icon-panel-btn" onClick={openNewChat} title="Новый чат"><MessageSquarePlus size={20} /></button>
          )}
          <button className={'icon-panel-btn' + (showArchive ? ' active' : '')} onClick={() => { if (showArchive) setShowArchive(false); else loadArchived(); }} title={showArchive ? 'Скрыть архив' : 'Архив'}>
            {showArchive ? <ArchiveRestore size={20} /> : <Archive size={20} />}
          </button>
          <button className={'icon-panel-btn' + (pushEnabled ? ' push-on' : ' push-off')} onClick={pushEnabled ? disablePush : enablePush} title={pushEnabled ? 'Отключить уведомления' : 'Включить уведомления'}>
            {pushEnabled ? <Bell size={20} /> : <BellOff size={20} />}
          </button>
          <button className="icon-panel-btn" onClick={() => navigate('/settings')} title="Настройки"><Settings size={20} /></button>
          {user.role === 'admin' && (
            <button className="icon-panel-btn" onClick={() => navigate('/admin')} title="Панель управления"><Shield size={20} /></button>
          )}
        </div>
        <div className="icon-panel-bottom">
          <button className="icon-panel-logout" onClick={handleLogout} title="Выйти"><Power size={20} /></button>
        </div>
      </div>

      {/* ====== Chat List ====== */}
      <div className="chat-list-panel" style={{ display: sidebarVisible ? 'flex' : 'none' }}>
        <div className="chat-list-search">
          <input
            placeholder="Поиск чата..."
            value={chatListFilter}
            onChange={(e) => setChatListFilter(e.target.value)}
          />
        </div>

        <div className="chat-list-items">
          {showArchive && (
            <div className="archive-section">
              <div className="archive-header">Архив</div>
              {archivedRooms.map((room) => {
                const name = room.name || (room.other_members && room.other_members.map((m) => m.name).join(', ')) || 'Чат';
                return (
                  <div key={room.id} className="archive-item">
                    <span>{name}</span>
                    <button onClick={() => unarchiveRoom(room.id)}>Восстановить</button>
                  </div>
                );
              })}
              {archivedRooms.length === 0 && <p className="archive-empty">Архив пуст</p>}
            </div>
          )}

          {!showArchive && (() => {
            const renderRoomItem = (room) => {
              const name = getRoomName(room);
              const isOnline = room.type === 'direct' && room.other_members?.[0] && onlineUsers.has(room.other_members[0].id);
              return (
                <div
                  key={room.id}
                  className={'chat-list-item' + (room.id === activeRoom ? ' active' : '')}
                  onClick={() => selectRoom(room.id)}
                  onContextMenu={(e) => handleRoomContextMenu(e, room)}
                >
                  <div className={'chat-list-avatar' + (room.other_members?.[0]?.role === 'doctor' ? ' role-doctor' : room.other_members?.[0]?.role === 'admin' ? ' role-admin' : room.other_members?.[0]?.role === 'assistant' ? ' role-assistant' : '')}>
                    <img src={getRoomAvatarSrc(room, name)} alt="" />
                    {isOnline && <div className="online-dot" />}
                    {room.other_members?.[0]?.role === 'doctor' && <div className="role-badge doctor" title="Врач">⚕</div>}
                    {room.other_members?.[0]?.role === 'admin' && <div className="role-badge admin" title="Админ">★</div>}
                    {room.other_members?.[0]?.role === 'assistant' && <div className="role-badge assistant" title="Ассистент">✦</div>}
                  </div>
                  <div className="chat-list-info">
                    <div className="chat-list-name">{name}</div>
                    <div className="chat-list-last">{stripHtml(room.last_message_body) || 'Нет сообщений'}</div>
                  </div>
                  <div className="chat-list-meta">
                    {room.last_message_body && <span className="chat-list-time">{formatTime(room.last_message_at)}</span>}
                    <div className="chat-list-meta-bottom">
                      {room.muted_at && <BellOff size={14} className="chat-list-muted" />}
                      {((room.unread_count || 0) > 0 || room.marked_unread) && <span className="chat-list-unread">{Math.max(room.unread_count || 0, 1)}</span>}
                    </div>
                  </div>
                </div>
              );
            };

            return (
              <>
                {chatGroups.map((group) => {
                  const visibleRooms = (group.rooms || [])
                    .map((id) => roomById.get(id))
                    .filter(Boolean)
                    .sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0));
                  if (visibleRooms.length === 0 && chatListFilter) return null;
                  const unreadSum = visibleRooms.reduce((s, r) => s + (r.unread_count || 0), 0);
                  const isCollapsed = collapsedGroups.has(group.id);
                  return (
                    <div key={group.id} className="chat-group">
                      <div className="chat-group-header" onClick={() => toggleGroupCollapsed(group.id)}>
                        <div className="chat-group-header__avatar">
                          {group.avatar_url
                            ? <img src={group.avatar_url} alt={group.name} />
                            : <div className="chat-group-header__placeholder">{(group.name || '?').slice(0, 1).toUpperCase()}</div>}
                        </div>
                        <div className="chat-group-header__body">
                          <div className="chat-group-header__name">{group.name}</div>
                          <div className="chat-group-header__meta">
                            {visibleRooms.length} {visibleRooms.length === 1 ? 'чат' : 'чатов'}
                            {unreadSum > 0 && <span className="chat-group-header__unread">{unreadSum}</span>}
                          </div>
                        </div>
                        <div className={'chat-group-header__caret' + (isCollapsed ? '' : ' is-open')}>▾</div>
                      </div>
                      {!isCollapsed && (
                        <div className="chat-group-items">
                          {visibleRooms.map((r) => renderRoomItem(r))}
                        </div>
                      )}
                    </div>
                  );
                })}
                {ungroupedRooms.map(renderRoomItem)}
              </>
            );
          })()}
        </div>
      </div>

      {/* ====== Chat Area ====== */}
      {activeRoom && currentRoom ? (
        <div className="chat-area">
          {/* Custom Header */}
          <div className="chat-header">
            <button className="chat-header-back" onClick={() => setSidebarVisible(true)}><ArrowLeft size={20} /></button>
            <img
              className="chat-header-avatar"
              src={getRoomAvatarSrc(currentRoom, getRoomName(currentRoom))}
              alt=""
            />
            <div
              className="chat-header-info"
              onClick={() => { if (currentRoom.type !== 'direct') setMembersPopup(!membersPopup); }}
              style={currentRoom.type !== 'direct' ? { cursor: 'pointer' } : {}}
            >
              <div className="chat-header-name">{getRoomName(currentRoom)}</div>
              <div className="chat-header-status">
                {currentRoom.type === 'direct' && currentRoom.other_members?.[0]
                  ? (onlineUsers.has(currentRoom.other_members[0].id) ? 'В сети' : 'Не в сети')
                  : `${(currentRoom.other_members?.length || 0) + 1} участников`
                }
              </div>
            </div>
            <div className="chat-header-actions">
              <button className="chat-header-btn" onClick={() => toggleMuteRoom(currentRoom.id)} title={currentRoom.muted_at ? 'Включить уведомления' : 'Отключить уведомления'}>
                {currentRoom.muted_at ? <BellOff size={18} /> : <Bell size={18} />}
              </button>
              <button className="chat-header-btn" onClick={() => { setSearchOpen(!searchOpen); setSearchResults([]); setSearchQuery(''); }}>
                {searchOpen ? <X size={18} /> : <Search size={18} />}
              </button>
              {(user.role === 'doctor' || user.role === 'admin' || user.role === 'assistant') && currentRoom.type === 'direct' && getClientId() && (
                <button className="chat-header-btn" onClick={() => setClientInfoVisible(!clientInfoVisible)}>
                  {clientInfoVisible ? <X size={18} /> : <Info size={18} />}
                </button>
              )}
            </div>
          </div>

          {/* Search dropdown */}
          {searchOpen && (
            <div className="chat-search-dropdown">
              <input
                className="search-input"
                placeholder="Поиск по сообщениям..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                autoFocus
              />
              {searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.map((m) => (
                    <div key={m.id} className="search-result-item">
                      <span className="search-result-sender">{m.sender_name}</span>
                      <span className="search-result-text">{(m.body || '').substring(0, 80)}</span>
                      <span className="search-result-date">{new Date(m.created_at).toLocaleDateString('ru-RU')}</span>
                    </div>
                  ))}
                </div>
              )}
              {searchQuery.length >= 2 && searchResults.length === 0 && (
                <div className="search-empty">Ничего не найдено</div>
              )}
            </div>
          )}

          {/* Messages */}
          <div className="chat-messages" ref={chatMessagesRef}>
            <MessageList
              typingIndicator={activeTyping.length > 0 ? <TypingIndicator content={`${activeTyping.join(', ')} печатает...`} /> : null}
              onYReachStart={loadMore}
              loading={loadingMessages}
            >
              {loadingMessages && hasMore[activeRoom] && (
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  <span className="loading-spinner">Загрузка...</span>
                </div>
              )}
              {groupedMessages.map((item, idx) => {
                if (item.type === 'separator') {
                  return <MessageSeparator key={`sep-${idx}`} content={formatDate(item.date)} />;
                }
                const msg = item.data;
                const isOwn = msg.sender_id === user.id;
                const isDeleted = !!msg.deleted_at;

                return (
                  <Message
                    key={msg.id}
                    model={{ direction: isOwn ? 'outgoing' : 'incoming', position: 'single', type: 'custom' }}
                    className={`${msg._failed ? 'message-failed' : ''} ${msg._sending ? 'message-sending' : ''} ${!isDeleted && msg.attachments?.length > 0 && msg.attachments.every(a => a.mime_type?.startsWith('image/')) && msg.type === 'file' ? 'message-has-image' : !isDeleted && msg.attachments?.length > 0 && msg.attachments.some(a => a.mime_type?.startsWith('video/')) && msg.type === 'file' ? 'message-has-video' : ''}`}
                    onContextMenu={(e) => handleContextMenu(e, msg)}
                    onTouchStart={(e) => startLongPress(e, msg)}
                    onTouchEnd={cancelLongPress}
                    onTouchMove={cancelLongPress}
                    onTouchCancel={cancelLongPress}
                  >
                    {!isOwn && <Message.Header sender={msg.sender_name} />}
                    <Message.CustomContent>
                      {isDeleted ? (
                        <span style={{ opacity: 0.5 }}>🚫 Сообщение удалено</span>
                      ) : (
                        <>
                          {msg.type === 'file' && msg.body && msg.body.includes('Переслано от') && (
                            <div className="forwarded-label" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.body.replace(/\n.*$/, '')) }} />
                          )}
                          {msg.attachments?.length > 0 && msg.attachments.map((att) => {
                            const url = `/uploads/${att.key}`;
                            const thumbUrl = att.thumbnail_key ? `/uploads/${att.thumbnail_key}` : url;
                            const previewUrl = att.preview_key ? `/uploads/${att.preview_key}` : url;
                            if (att.mime_type?.startsWith('image/')) {
                              return (
                                <div key={att.id} className="chat-image-wrap">
                                  <img
                                    src={thumbUrl}
                                    alt={att.filename}
                                    className="chat-image"
                                    loading="lazy"
                                    onClick={() => {
                                      // Эмулированный click после long-press: не открываем lightbox,
                                      // флаг сбросит window-обработчик в useEffect выше.
                                      if (longPressFiredRef.current) return;
                                      setLightboxImage(previewUrl);
                                    }}
                                  />
                                  <a href={url} download={att.filename} className="chat-image-download" title="Скачать"><Download size={16} /></a>
                                </div>
                              );
                            }
                            if (att.mime_type?.startsWith('video/')) {
                              return (
                                <VideoAttachment key={att.id} url={url} filename={att.filename} size={att.size} />
                              );
                            }
                            return (
                              <a key={att.id} href={url} download={att.filename} className="chat-file" target="_blank" rel="noopener noreferrer">
                                📎 {att.filename} ({(att.size / 1024).toFixed(0)} КБ)
                              </a>
                            );
                          })}
                          {msg.type !== 'file' && msg.body && (msg.body.includes('<') ? <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.body) }} /> : <div>{msg.body}</div>)}
                          {msg.type === 'file' && msg.body && !msg.body.includes('Переслано от') && !msg.attachments?.some(a => a.filename === msg.body) && (
                            msg.body.includes('<') ? <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.body) }} /> : <div>{msg.body}</div>
                          )}
                          {msg._failed && <button className="retry-btn" onClick={() => retryMessage(msg)}>Повторить</button>}
                        </>
                      )}
                    </Message.CustomContent>
                    <Message.Footer sentTime={`${formatTime(msg.created_at)}${msg.updated_at ? ' (ред.)' : ''}`} sender={msg._failed ? '⚠️ Не отправлено' : ''} />
                  </Message>
                );
              })}
            </MessageList>
            {showScrollDown && (
              <button
                className="scroll-down-btn"
                onClick={() => {
                  scrollToBottom();
                  setShowScrollDown(false);
                }}
                title="К последнему сообщению"
              >
                ↓
              </button>
            )}
          </div>

          {/* Input */}
          <div className="chat-input-area">
            <FileUpload onUploaded={handleFileUploaded} />
            <ChatMessageInput placeholder="Введите сообщение..." onSend={handleSend} onTyping={handleTyping} />
          </div>
        </div>
      ) : (
        <div className="chat-empty">
          <div className="chat-empty-icon"><MessageSquarePlus size={48} /></div>
          <p>Выберите чат для начала общения</p>
        </div>
      )}

      {/* Client Info Panel */}
      {clientInfoVisible && (user.role === 'doctor' || user.role === 'admin' || user.role === 'assistant') && (
        <ClientInfo clientId={getClientId()} visible={clientInfoVisible} onClose={() => setClientInfoVisible(false)} />
      )}

      {/* ====== Modals ====== */}

      {/* Room context menu */}
      {roomContextMenu && (
        <div className="context-menu" style={{ top: roomContextMenu.y, left: roomContextMenu.x }}>
          <button onClick={() => markRoomUnread(roomContextMenu.room.id)}>Пометить непрочитанным</button>
          <button onClick={() => archiveRoom(roomContextMenu.room.id)}>В архив</button>
          {user.role === 'admin' && (
            <button onClick={() => deleteRoomAction(roomContextMenu.room.id)} style={{color:'#AD182C'}}>Удалить чат</button>
          )}
        </div>
      )}

      {/* Message context menu */}
      {contextMenu && (
        <div className="context-menu" style={{ top: contextMenu.y, left: contextMenu.x }}>
          {contextMenu.msg.body && contextMenu.msg.type !== 'file' && (
            <button onClick={() => handleCopy(contextMenu.msg)}><Copy size={14} style={{marginRight:6}} /> Копировать</button>
          )}
          {contextMenu.isOwn && contextMenu.msg.type !== 'file' && (
            <button onClick={() => handleEditStart(contextMenu.msg)}><Pencil size={14} style={{marginRight:6}} /> Редактировать</button>
          )}
          <button onClick={() => handleForward(contextMenu.msg)}><Forward size={14} style={{marginRight:6}} /> Переслать</button>
          {contextMenu.msg.attachments?.length > 0 && (
            <button onClick={() => handleDownload(contextMenu.msg)}><Download size={14} style={{marginRight:6}} /> Скачать</button>
          )}
          {(contextMenu.isOwn || user.role === 'admin') && (
            <button onClick={() => handleDelete(contextMenu.msg)}><Trash2 size={14} style={{marginRight:6}} /> Удалить</button>
          )}
        </div>
      )}

      {/* Members popup */}
      {membersPopup && currentRoom && (
        <div className="modal-overlay" onClick={() => { setMembersPopup(false); setAddMemberMode(false); }}>
          <div className="members-popup" onClick={(e) => e.stopPropagation()}>
            {canEditRoomAvatar(currentRoom) && (
              <div className="members-avatar-edit">
                <div className="members-avatar-edit__preview">
                  {currentRoom.avatar_url
                    ? <img src={currentRoom.avatar_url} alt="" />
                    : <div className="members-avatar-edit__placeholder">{(getRoomName(currentRoom) || '?').slice(0, 1).toUpperCase()}</div>}
                </div>
                <div className="members-avatar-edit__actions">
                  <label className="admin-btn primary" style={{ cursor: 'pointer', margin: 0 }}>
                    {currentRoom.avatar_url ? 'Сменить аватар' : 'Загрузить аватар'}
                    <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={(e) => { uploadRoomAvatar(currentRoom.id, e.target.files?.[0]); e.target.value = ''; }} />
                  </label>
                  {currentRoom.avatar_url && <button className="admin-btn" onClick={() => removeRoomAvatar(currentRoom.id)}>Удалить</button>}
                </div>
              </div>
            )}
            <h3>Участники</h3>
            <div className="members-list">
              <div className="members-item"><span className="members-name">{user.name}</span><span className="members-role">Вы</span></div>
              {currentRoom.other_members && currentRoom.other_members.map((m) => (
                <div key={m.id} className="members-item">
                  <span className="members-name">{m.name}</span>
                  <span className="members-role">{m.role === 'admin' ? 'Админ' : m.role === 'doctor' ? 'Врач' : m.role === 'assistant' ? 'Ассистент' : 'Клиент'}</span>
                  {currentRoom.type !== 'direct' && (user.role === 'admin' || user.role === 'doctor' || user.role === 'assistant') && (
                    <button className="members-remove" onClick={() => handleRemoveMember(m.id)} title="Удалить"><UserMinus size={14} /></button>
                  )}
                </div>
              ))}
            </div>
            {currentRoom.type !== 'direct' && (user.role === 'admin' || user.role === 'doctor' || user.role === 'assistant') && !addMemberMode && (
              <button className="members-add-btn" onClick={openAddMember}><UserPlus size={14} /> Добавить участника</button>
            )}
            {addMemberMode && (
              <div className="members-add-list">
                {addMemberUsers.map((u) => (
                  <button key={u.id} className="members-add-item" onClick={() => handleAddMember(u.id)}>
                    {u.name} <span className="members-add-role">({u.role === 'admin' ? 'Админ' : u.role === 'doctor' ? 'Врач' : u.role === 'assistant' ? 'Ассистент' : 'Клиент'})</span>
                  </button>
                ))}
                {addMemberUsers.length === 0 && <p className="members-add-empty">Все пользователи уже в чате</p>}
              </div>
            )}
            <button className="members-close" onClick={() => { setMembersPopup(false); setAddMemberMode(false); }}>Закрыть</button>
          </div>
        </div>
      )}

      {/* Forward message modal */}
      {forwardMessage && (
        <div className="modal-overlay" onClick={() => setForwardMessage(null)}>
          <div className="new-chat-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Переслать в чат</h3>
            <div className="new-chat-list">
              {rooms.filter((r) => r.id !== activeRoom).map((r) => (
                <button key={r.id} className="new-chat-user" onClick={() => forwardToRoom(r.id)}>
                  <span className="new-chat-name">{getRoomName(r)}</span>
                </button>
              ))}
              {rooms.filter((r) => r.id !== activeRoom).length === 0 && <p className="new-chat-empty">Нет доступных чатов</p>}
            </div>
            <button className="new-chat-close" onClick={() => setForwardMessage(null)}>Отмена</button>
          </div>
        </div>
      )}

      {/* New chat modal */}
      {newChatModal && (
        <div className="modal-overlay" onClick={() => setNewChatModal(false)}>
          <div className="new-chat-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Новый чат</h3>
            <div className="new-chat-list">
              {allUsers.map((u) => (
                <button key={u.id} className="new-chat-user" onClick={() => createDirectChat(u.id)}>
                  <span className="new-chat-name">{u.name}</span>
                  <span className="new-chat-role">{u.role === 'admin' ? 'Админ' : u.role === 'doctor' ? 'Врач' : 'Клиент'}</span>
                </button>
              ))}
              {allUsers.length === 0 && <p className="new-chat-empty">Нет пользователей</p>}
            </div>
            <button className="new-chat-close" onClick={() => setNewChatModal(false)}>Отмена</button>
          </div>
        </div>
      )}

      {/* Edit bar */}
      {editingMessage && (
        <div className="edit-bar">
          <textarea
            ref={editTextareaRef}
            className="edit-bar-input"
            value={editingMessage.body}
            onChange={(e) => {
              setEditingMessage({ ...editingMessage, body: e.target.value });
              setTimeout(autoResizeEditTextarea, 0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleEditSave(editingMessage.body);
              }
              if (e.key === 'Escape') handleEditCancel();
            }}
            autoFocus
          />
          <button className="edit-bar-save" onClick={() => handleEditSave(editingMessage.body)}>✓</button>
          <button className="edit-bar-cancel" onClick={handleEditCancel}>✕</button>
        </div>
      )}

      {/* Lightbox */}
      {lightboxImage && <ImageLightbox src={lightboxImage} onClose={() => setLightboxImage(null)} />}
    </div>
  );
}
